import { useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AnimatePresence, motion } from 'motion/react';
import { useInView } from 'react-intersection-observer';
import { useShallow } from 'zustand/react/shallow';

import {
  ReviewDetailsCard,
  ReviewDetailsItem,
} from '@/features/review-details';

import { VabsPaymentSourceOfFunds } from '@/shared/api';
import { ArrowDownSlim } from '@/shared/assets';
import { getValueWithCurrency } from '@/shared/lib';
import { Button, Input, Typography } from '@/shared/ui';
import { VabsSFSelect } from '@/shared/ui/vabs-sf-select';

import { useSendMoney } from '../../../../model/useSendMoney';
import {
  getFieldName,
  serializeFormToInput,
  serializeInnerFormToInput,
} from '../lib';
import { createPaymentMutation } from '../model';
import { AccountView } from './account';
import { COMPANY_NAME_REGEX } from '../../../../config';

export const ReviewDetails = () => {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [sf, setSF] = useState<VabsPaymentSourceOfFunds>(null);
  const [purpose, setPurpose] = useState('');
  const [isLoading, setLoading] = useState(false);
  const { setStep, data, amount, updateFormData } = useSendMoney(
    useShallow((state) => state),
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createPaymentMutation,
  });
  const sendButtonRef = useRef<HTMLButtonElement | null>(null);
  const [ref, inView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (data?.sourceOfFunds) {
      setSF(data.sourceOfFunds);
    }
    if (data?.purposeOfPayment) {
      setPurpose(data.purposeOfPayment);
    }
  }, []);

  const onPurposeChange = (v: string) => {
    setPurpose(v);
    updateFormData({ purposeOfPayment: v });
  };

  const onSFSelect = (v: VabsPaymentSourceOfFunds) => {
    setSF(v);
    updateFormData({ sourceOfFunds: v });
  };

  const onSend = async () => {
    if (data?.isInner) {
      try {
        setLoading(true);
        const res = await mutateAsync(serializeInnerFormToInput(data));
        if (res?.id) {
          updateFormData({ id: res.id });
          await queryClient.invalidateQueries({
            queryKey: ['txs-list'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['vabs-accounts'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['payments-list'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['faves-list'],
          });
          setLoading(false);
          setStep('success');
        } else {
          setStep('error');
        }
      } catch (e) {
        console.log(e);
        setLoading(false);
        setStep('error');
      }
      return;
    }
    if (!sf) {
      setError('Please provide source of funds');
      return;
    }
    if (purpose && !COMPANY_NAME_REGEX.test(purpose)) {
      setError('Invalid format')
      return
    }
    if (!purpose) {
      setError('Please enter purpose of payment');
      return;
    }
    if (data) {
      try {
        setLoading(true);
        const res = await mutateAsync(serializeFormToInput(data));
        if (res?.id) {
          updateFormData({ id: res.id });
          await queryClient.invalidateQueries({
            queryKey: ['txs-list'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['vabs-accounts'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['payments-list'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['faves-list'],
          });
          setLoading(false);
          setStep('success');
        } else {
          setStep('error');
        }
      } catch (e) {
        console.log(e);
        setLoading(false);
        setStep('error');
      }
    }
  };

  const scrollToSendButton = () => {
    requestAnimationFrame(() => {
      sendButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    });
  };

  const accountHolder = data?.companyName
    ? data?.companyName
    : `${data?.firstName} ${data?.lastName}`;

  return (
    <div className="mt-5 flex w-full flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col gap-y-1">
        <ReviewDetailsCard
          title={
            data?.isInner
              ? 'RECIPIENT DETAILS'
              : `${data?.account?.code || ''} BANK DETAILS`
          }
          onEdit={
            !data?.isInner
              ? () => {
                setStep('info');
              }
              : undefined
          }
        >
          {!data?.isInner && (
            <>
              <ReviewDetailsItem
                title={getFieldName(data?.paymentSystem, 'accountNumber')}
                value={data?.accountNumber}
              />
              <ReviewDetailsItem
                title={getFieldName(data?.paymentSystem, 'bankCode')}
                value={data?.bankCode}
              />
            </>
          )}
          {data?.companyName && !data?.isInner && (
            <ReviewDetailsItem title="Company name" value={data?.companyName} />
          )}
          {data?.companyName && data?.isInner && (
            <ReviewDetailsItem
              title="Account holder name"
              value={data?.companyName}
            />
          )}
          {data?.firstName && (
            <ReviewDetailsItem
              title="Account holder name"
              value={accountHolder}
            />
          )}
          {data?.innerEmail && (
            <ReviewDetailsItem title="Email" value={data?.innerEmail} />
          )}
          {data?.innerPhone && (
            <ReviewDetailsItem title="Mobile number" value={data?.innerPhone} />
          )}
        </ReviewDetailsCard>
        {!!data?.intermediaryBank?.accountNumber && (
          <ReviewDetailsCard
            title={`INTERMEDIARY BANK DETAILS`}
            onEdit={() => {
              setStep('info');
            }}
          >
            <ReviewDetailsItem
              title="Bank account number"
              value={data?.intermediaryBank?.accountNumber}
            />
            <ReviewDetailsItem
              title="Bank code"
              value={data?.intermediaryBank?.swift}
            />
            <ReviewDetailsItem
              title="Bank name"
              value={data?.intermediaryBank?.bankInfo?.name}
            />
          </ReviewDetailsCard>
        )}
        <ReviewDetailsCard
          title="YOUR TRANSFER"
          onEdit={() => {
            setStep('amount');
          }}
        >
          <ReviewDetailsItem
            title="You send"
            value={getValueWithCurrency(
              data?.totalAmount,
              data?.account?.symbol,
            )}
          />
          <ReviewDetailsItem
            title="Total fees (included)"
            value={getValueWithCurrency(data?.fee, data?.account?.symbol)}
          />
          <ReviewDetailsItem
            title={`${accountHolder} gets`}
            value={getValueWithCurrency(data?.amount, data?.account?.symbol)}
          />
        </ReviewDetailsCard>
        <AccountView ticker={data?.account?.code} className="mb-2" />
        <Input
          value={purpose}
          onValueChange={onPurposeChange}
          placeholder={
            data?.isInner ? 'Reference (optional)' : 'Purpose of payment'
          }
        />
        {!data?.isInner && <VabsSFSelect value={sf} onSelect={onSFSelect} />}
        {error && (
          <Typography variant="14-r" className="mx-2 text-negative-500">
            {error}
          </Typography>
        )}
        {/* <PaymentMethodSelect
          type={paymentMethod}
          onSelect={setPaymentMethod}
          nativeMethods={nativeSendBusinessItems}
        /> */}
      </div>
      <AnimatePresence>
        {!inView && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              className="absolute bottom-1 right-0 h-14 w-14 rounded-full p-0"
              variant="brand-500"
              onClick={scrollToSendButton}
            >
              <ArrowDownSlim className="min-h-6 min-w-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="mt-5 pb-1">
        <Button
          ref={(el) => {
            sendButtonRef.current = el;
            ref(el);
          }}
          className="w-full"
          variant="brand-500"
          size="lg"
          loading={isPending || isLoading}
          disabled={isPending || isLoading}
          onClick={onSend}
        >
          {`Send ${getValueWithCurrency(data?.totalAmount, data?.account?.symbol)}`}
        </Button>
      </div>
    </div>
  );
};
